import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroBanners } from '@/mocks/dramas';

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrent(index);
      setTimeout(() => setIsTransitioning(false), 600);
    },
    [isTransitioning]
  );

  const next = useCallback(() => {
    goTo((current + 1) % heroBanners.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const banner = heroBanners[current];

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      {heroBanners.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <img
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60 z-20"></div>

      <div className="absolute inset-0 z-30 flex items-end pb-12 md:pb-16">
        <div className="w-full mx-auto max-w-[1400px] px-4 md:px-6">
          <div className="max-w-lg">
            <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-500 text-white mb-4 whitespace-nowrap">
              {banner.tag}
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-white mb-3">
              {banner.title}
            </h2>
            <p className="text-sm md:text-base text-white/80 mb-5 leading-relaxed">
              {banner.description}
            </p>
            <div className="flex items-center gap-4">
              <Link
                to={`/drama/${banner.id}`}
                onClick={handleClick}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line text-lg"></i>
                立即观看
              </Link>
              <span className="flex items-center gap-1 text-white/90 text-sm">
                <i className="ri-star-fill text-accent-500"></i>
                {banner.rating}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-6 z-30 flex gap-2">
        {heroBanners.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
              index === current
                ? 'bg-primary-500 w-6'
                : 'bg-white/50 hover:bg-white/80'
            }`}
            aria-label={`切换到第 ${index + 1} 张`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + heroBanners.length) % heroBanners.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-white transition-all duration-300 cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-white transition-all duration-300 cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>
    </section>
  );
}